/**
 * Multi-backend log writer.
 *
 * Writes log events to multiple backends simultaneously.
 * Used when the configured backend choice is "both".
 */

import type { BaseLogEntry } from "../types";
import type { LogBackend } from "./interface";
import { logDevError } from "./log-dev-error";

/**
 * Multi-backend implementation.
 * Writes to all configured backends in sequence.
 */
export class MultiLogBackend implements LogBackend {
  private backends: LogBackend[];

  /**
   * Create a new multi-backend writer.
   *
   * @param backends - Array of backends to write to
   */
  constructor(backends: LogBackend[]) {
    this.backends = backends;
  }

  /**
   * Write a log entry to all backends.
   *
   * Each backend write is wrapped in try/catch to ensure
   * one failing backend doesn't prevent others from receiving the event.
   *
   * @param entry - The log entry to write
   */
  write(entry: BaseLogEntry): void {
    for (const backend of this.backends) {
      try {
        backend.write(entry);
      } catch (err) {
        // Silent fail - one backend failing shouldn't affect others
        logDevError("MultiLogBackend", "write", err);
      }
    }
  }

  /**
   * Flush all backends that support flushing.
   *
   * @returns Promise that resolves when all backends have flushed
   */
  async flush(): Promise<void> {
    const flushPromises = this.backends
      .filter(
        (backend): backend is LogBackend & { flush: () => Promise<void> } =>
          typeof backend.flush === "function",
      )
      .map((backend) =>
        backend.flush().catch((err: unknown) => {
          // Silent fail - one backend failing shouldn't affect others
          logDevError("MultiLogBackend", "flush", err);
        }),
      );

    await Promise.all(flushPromises);
  }
}
